/**
 * L'encodage d'un QR, sous une forme que l'écran ET le PDF savent dessiner.
 *
 * ## Pourquoi une matrice plutôt qu'une image
 *
 * `qrcode` sait rendre un PNG, un canvas, un SVG. Aucun des trois ne convient
 * aux deux destinations : le PDF a besoin de rectangles vectoriels — un QR en
 * PNG s'imprime flou et certains lecteurs le refusent —, et l'écran a besoin
 * d'un SVG qui suive le zoom.
 *
 * On expose donc la **matrice de modules**, et chaque destination la dessine
 * avec ses moyens. C'est aussi ce qui permet de tester l'encodage sans
 * rasteriser quoi que ce soit.
 *
 * ## Le niveau de correction
 *
 * `M` — 15 % de redondance. C'est celui que recommande le European Payments
 * Council pour les QR de virement : assez pour un écran de téléphone un peu
 * sale ou un papier plié, sans gonfler le code au point de le rendre dense à
 * scanner.
 */

import QRCode from 'qrcode';

export interface MatriceQr {
  /** Le nombre de modules par côté, zone de silence NON comprise. */
  taille: number;
  /** `true` = module sombre. Indexé `ligne * taille + colonne`. */
  modules: boolean[];
}

/**
 * La zone de silence exigée par la norme : quatre modules tout autour.
 *
 * Sans elle, beaucoup de lecteurs échouent — et l'échec est silencieux, le
 * téléphone ne trouve simplement rien.
 */
export const SILENCE = 4;

export function matriceQr(texte: string): MatriceQr {
  const qr = QRCode.create(texte, { errorCorrectionLevel: 'M' });
  const taille = qr.modules.size;
  const data = qr.modules.data;
  const modules: boolean[] = new Array(taille * taille);
  for (let i = 0; i < taille * taille; i++) modules[i] = data[i] === 1;
  return { taille, modules };
}

export function estSombre(m: MatriceQr, ligne: number, colonne: number): boolean {
  if (ligne < 0 || colonne < 0 || ligne >= m.taille || colonne >= m.taille) return false;
  return m.modules[ligne * m.taille + colonne];
}

/**
 * Le QR en SVG, pour l'écran.
 *
 * Un seul chemin `<path>` plutôt qu'un rectangle par module : un QR de
 * version 4 compte 1 089 modules, et autant d'éléments DOM ralentissent un
 * téléphone pour rien.
 */
export function qrEnSvg(texte: string, opts: { taillePx?: number; sombre?: string } = {}): string {
  const m = matriceQr(texte);
  const cote = m.taille + 2 * SILENCE;
  const px = opts.taillePx ?? 240;
  const sombre = opts.sombre ?? '#0f172a';

  let chemin = '';
  for (let l = 0; l < m.taille; l++) {
    for (let c = 0; c < m.taille; c++) {
      if (estSombre(m, l, c)) chemin += `M${c + SILENCE} ${l + SILENCE}h1v1h-1z`;
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" `
    + `viewBox="0 0 ${cote} ${cote}" shape-rendering="crispEdges" role="img" `
    + `aria-label="Code QR de paiement">`
    + `<rect width="${cote}" height="${cote}" fill="#ffffff"/>`
    + `<path d="${chemin}" fill="${sombre}"/>`
    + `</svg>`
  );
}

/** Le SVG prêt à mettre dans un `src` d'image, ou à télécharger. */
export function qrEnDataUrl(texte: string, opts?: { taillePx?: number; sombre?: string }): string {
  /*
    `encodeURIComponent` et non base64 : un SVG encodé ainsi reste lisible dans
    l'inspecteur, et le résultat est plus court.
  */
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrEnSvg(texte, opts))}`;
}
